<template>
    <div class="vx-w-full">
        <div class="vx-flex vx-flex-col vx-gap-4 vx-mb-6">
            <vx-input v-model="newGroup" @paste="onPaste" @keydown.enter="onEnterInput"
                placeholder="Please enter to add"
                :hint="`Paste or press enter to add up to ${restrictions.MAX_GROUPS_PER_CONTACT} groups separated by comma.`"
                >
            </vx-input>
        </div>

        <vx-popover hover arrow placement="top" v-for="group in props.selectedGroups" :key="group.group_id">
            <vx-badge :color="group.invalid ? 'rose' : 'primary'" size="sm"
                class="vx-mr-2 vx-mb-1">
                <span>
                    {{ group.group_id }}
                </span>
                <font-awesome-icon class="vx-cursor-pointer" @click="emit('remove', group.group_id)"
                    :icon="faTimes"></font-awesome-icon>
            </vx-badge>
            <template v-slot:content v-if="group.invalid">
                {{ group.invalid }}
            </template>
        </vx-popover>
    </div>
</template>
<script setup>
import { VxInput, VxPopover, VxBadge } from '@voxie/frontend-components';
import { ref } from 'vue';
import { restrictions } from '../../../constants/groups';
import { validate } from '../../../utils/groups';
import { useToasted } from '../../../composables/useToasted';
import { uniqBy } from '~/utils/helpers';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faTimes } from '@fortawesome/pro-solid-svg-icons';

const $toasted = useToasted();
const props = defineProps({
    selectedGroups: {
        type: Array,
        required: true
    },
});

const emit = defineEmits(['add', 'remove']);

const newGroup = ref('');

const mapGroups = (input) => {
    if (!input || input?.trim().length <= 0) {
        return [];
    }

    return uniqBy(input.split(/\r?\n|\r|\n|,/g).map((groupId) => {
        if (!groupId?.trim()?.length) {
            return null;
        }

        const groupIdNormalized = groupId.trim().toLowerCase();
        const isValid = validate({ group_id: groupIdNormalized })
        return {
            group_id: groupIdNormalized,
            invalid: !isValid && `It was not possible to process the group ${groupIdNormalized}.` || null,
        }
    }).filter((group) => group !== null), 'group_id');
}

const onEnterInput = (event) => {
    event.preventDefault();
    addGroups(newGroup.value);
};

const onPaste = (event) => {
    event.preventDefault();
    const text = event.clipboardData.getData('text');
    addGroups(text);
};

const addGroups = (text) => {
    let newGroups = mapGroups(text);

    if (newGroups.length <= 0) {
        return;
    }

    if (props.selectedGroups.length + newGroups.length > restrictions.MAX_GROUPS_PER_CONTACT) {
        groupsLimitReachedAlert();
        const groupsToSlice = newGroups.length - (newGroups.length - restrictions.MAX_GROUPS_PER_CONTACT) - props.selectedGroups.length;
        newGroups = newGroups.slice(0, groupsToSlice);
    }

    processGroups(newGroups);
}

const processGroups = (groups) => {
    groups.forEach((g) => {
        emit('add', g);
    });

    newGroup.value = null;
}

const limitReachedMessage = `A contact cannot have more than ${restrictions.MAX_GROUPS_PER_CONTACT} groups.`;
const groupsLimitReachedAlert = () => {
    $toasted.show(limitReachedMessage);
};

</script>
